import compact from "lodash/compact";
import find from "lodash/find";
import moment from "moment";
import { TemplateKey } from "PFApp/constants/templates";
import { useBookingCategoryLink } from "PFApp/hooks/activity_form/use_booking_category_link";
import { useHasSubtemplates } from "PFApp/hooks/use_has_subtemplates";
import { useTemplate } from "PFApp/hooks/use_template";
import { FormFieldData } from "PFApp/types/activity_form/activity_form_schema";
import { getVisibleProperties } from "PFCore/helpers/templates";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { useSubtemplatesDefaultAPICustomValues } from "PFCore/hooks/queries/subtemplates/use_subtemplates_default_api_custom_values";
import { Activity, Profile, Subtemplate, Template } from "PFTypes";
import { useCallback } from "react";

import { useGetCustomFieldsToLink } from "../use_get_custom_fields_to_link";
import { useSubtemplateDefaultValues } from "../use_subtemplate_default_values";
import { ActivityWithDefaultAttributes } from "./activity_form_default_attributes.types";
import {
  fillPropertyWithDefaultValue,
  getActivityWithoutBlockedCustomValues
} from "./activity_form_default_attributes.utils";

type UseActivityFormDefaultAttributes = {
  template: Template | undefined;
  parentActivity?: Activity;
};

type GetActivityFormDefaultAttributes = (params: {
  subtemplate: Subtemplate | null;
  isClone: boolean;
  subtemplates?: Subtemplate[];
  attributes: ActivityWithDefaultAttributes;
  activityCreator: Profile | null;
}) => ActivityWithDefaultAttributes;

export const useActivityFormDefaultAttributes = ({
  template,
  parentActivity
}: UseActivityFormDefaultAttributes) => {
  const { data: currentProfile } = useCurrentProfile();
  const { customTypes } = useCustomTypes();

  const hasSubtemplates = useHasSubtemplates(template?.key);
  const { getSubtemplateDefaultValues } = useSubtemplateDefaultValues();
  const { getAPICustomValuesByText } = useSubtemplatesDefaultAPICustomValues({
    templateKey: template?.key as TemplateKey,
    enabled: !!template?.key
  });
  const parentTemplate = useTemplate({ id: parentActivity?.template_id });
  const getCustomFieldsToLink = useGetCustomFieldsToLink({ template: template!, parentTemplate });
  const { getBookingCategoryLinkHelpers } = useBookingCategoryLink({ template: template! });

  const getActivityFormDefaultAttributes: GetActivityFormDefaultAttributes = useCallback(
    ({ subtemplate, isClone, attributes, activityCreator }) => {
      if (!template) {
        return {};
      }

      const result = {
        custom_fields: [],
        metadata: {},
        ...attributes
      };

      result.post_until = moment().add(template.default_post_until_days, "days").toISOString();

      const unknownSchema = hasSubtemplates && !subtemplate;
      if (unknownSchema || isClone) {
        return getActivityWithoutBlockedCustomValues(result);
      }

      const { metadataFields, customFields } = subtemplate
        ? getSubtemplateDefaultValues({ subtemplate, activityCreator })
        : { metadataFields: [], customFields: [] };

      const customFieldsLinkedFromEngagement = parentActivity
        ? getCustomFieldsToLink({
            engagement: parentActivity,
            roleSubtemplate: subtemplate
          })
        : [];
      const { canLink: canLinkBookingCategory, parentBookingCategoryId } = getBookingCategoryLinkHelpers({
        parentActivity,
        activityCreator,
        subtemplate,
        bookingCategoryId: undefined
      });
      const fieldsLinkedFromEngagement: FormFieldData[] = compact([
        ...customFieldsLinkedFromEngagement.map(({ type, values }) => ({
          fieldName: type.name,
          values
        })),
        canLinkBookingCategory && !!parentBookingCategoryId
          ? {
              fieldName: "booking_category_id",
              values: parentBookingCategoryId
            }
          : null
      ]);

      getVisibleProperties(subtemplate || template).forEach((property) => {
        const subtemplateDefaultValue =
          find(metadataFields, ["fieldName", property.name])?.values ??
          getAPICustomValuesByText(
            property.name,
            find(customFields, ["fieldName", property.name])?.values || []
          );

        fillPropertyWithDefaultValue(result, {
          property,
          fieldsLinkedFromEngagement,
          subtemplateDefaultValue,
          customTypes,
          currentProfile,
          activityCreator
        });
      });

      return getActivityWithoutBlockedCustomValues(result);
    },
    [
      template,
      parentActivity,
      hasSubtemplates,
      currentProfile,
      getSubtemplateDefaultValues,
      getAPICustomValuesByText,
      customTypes,
      getBookingCategoryLinkHelpers,
      getCustomFieldsToLink
    ]
  );

  return { getActivityFormDefaultAttributes };
};
